// assets/src/js/features/header/mobileActions/components/MobileOffcanvas.tsx

import React, { useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import LanguageSwitcher from "../../language/components/LanguageSwitcher";
import { useGetCategoriesQuery } from "../../../product/api/productTaxonomyApi";
import { buildCategoryTree } from "../../../product/helpers/productsCategoryHelpers";
import type { CategoryWithChildren } from "../../../product/helpers/productsCategoryHelpers";
import type { TransformedWPCategory } from "../../../product/types/productTaxonomyApiTypes";
import {
  setMobileOffcanvasView,
  selectMobileOffcanvasView,
  selectMobileOffcanvasParentCategoryId,
} from "../../headerUiSlice";
import type { AppDispatch } from "../../../../app/store"; // Correctly import AppDispatch
import Skeleton from "react-loading-skeleton";

// Define interface for menu item structure
interface MobileMenuItem {
  id: number;
  title: string;
  url: string;
  target?: string;
  parentId: string; // Usually '0' for top-level
  order: number;
}

interface MobileOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode;
}

// Simple Close Icon SVG (Keep as is)
const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mobile-offcanvas-close-icon"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// Simple Back Icon SVG (Keep as is)
const BackIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16"
    aria-hidden="true"
    className="back-icon"
  >
    <path
      fillRule="evenodd"
      d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
    />
  </svg>
);

// Simple Arrow Icon for expansion indication (Keep as is)
const ExpandIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16"
    aria-hidden="true"
    className="expand-icon"
  >
    <path
      fillRule="evenodd"
      d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
    />
  </svg>
);

const MobileOffcanvas: React.FC<MobileOffcanvasProps> = ({
  isOpen,
  onClose,
  children,
}) => {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const currentView = useSelector(selectMobileOffcanvasView);
  const parentCategoryId = useSelector(selectMobileOffcanvasParentCategoryId);

  const mobileMenu: MobileMenuItem[] | undefined =
    window.themeVariables?.mobileMenu;

  const shopPageUrl = window.themeVariables?.shopPageUrl || "/shop/"; // Fallback if not defined

  const {
    data: categoriesData,
    isLoading: isLoadingCategories,
    error: categoriesError,
  } = useGetCategoriesQuery(undefined, { skip: !isOpen });

  const categoriesById: Record<number, TransformedWPCategory> | undefined =
    categoriesData?.categories;
  const categoriesArray: TransformedWPCategory[] = categoriesById
    ? Object.values(categoriesById)
    : [];

  const categoryTree = useMemo(
    () => buildCategoryTree(categoriesArray),
    [categoriesArray]
  );

  const currentCategoryList = useMemo(() => {
    const filterUncategorized = (categories: CategoryWithChildren[]) =>
      categories.filter((category) => category.slug !== "uncategorized");

    if (currentView !== "category" || !categoriesById) return [];
    if (parentCategoryId === null) {
      return filterUncategorized(categoryTree);
    } else {
      const findParent = (
        nodes: CategoryWithChildren[]
      ): CategoryWithChildren | null => {
        for (const node of nodes) {
          if (node.id === parentCategoryId) return node;
          const foundInChildren = findParent(node.children);
          if (foundInChildren) return foundInChildren;
        }
        return null;
      };
      const parentNode = findParent(categoryTree);
      return parentNode ? filterUncategorized(parentNode.children) : [];
    }
  }, [currentView, parentCategoryId, categoriesById, categoryTree]);

  const categoryViewTitle = useMemo(() => {
    if (currentView !== "category" || !categoriesById)
      return t("header.categories", "Categories");
    if (parentCategoryId === null) {
      return t("header.categories", "Categories");
    } else {
      const parent = categoriesById[parentCategoryId];
      return parent ? parent.name : t("header.subcategories", "Subcategories");
    }
  }, [currentView, parentCategoryId, categoriesById, t]);

  const handleCategoriesClick = () => {
    dispatch(setMobileOffcanvasView({ view: "category", parentId: null }));
  };

  const handleExpandClick = (categoryId: number) => {
    dispatch(
      setMobileOffcanvasView({ view: "category", parentId: categoryId })
    );
  };

  const handleBackClick = () => {
    if (currentView === "category") {
      if (parentCategoryId !== null && categoriesById) {
        const parent = categoriesById[parentCategoryId];
        const grandparentId =
          parent?.parent === 0 ? null : parent?.parent ?? null;
        dispatch(
          setMobileOffcanvasView({ view: "category", parentId: grandparentId })
        );
      } else {
        dispatch(setMobileOffcanvasView({ view: "main" }));
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const panelElement = panelRef.current;

    if (isOpen && panelElement) {
      document.addEventListener("keydown", handleKeyDown);
      const focusButton = panelElement.querySelector(
        currentView === "main"
          ? ".mobile-offcanvas-close-button"
          : ".mobile-offcanvas-back-button"
      ) as HTMLElement;
      focusButton?.focus();
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, currentView]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("mobile-offcanvas-open");
    } else {
      document.body.classList.remove("mobile-offcanvas-open");
    }
    return () => {
      document.body.classList.remove("mobile-offcanvas-open");
    };
  }, [isOpen]);

  return (
    <>
      <div
        className={`mobile-offcanvas-overlay ${isOpen ? "is-open" : ""}`}
        onClick={onClose}
        aria-hidden={!isOpen}
        tabIndex={-1}
      />

      <div
        ref={panelRef}
        className={`mobile-offcanvas-panel ${isOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-offcanvas-title"
        hidden={!isOpen}
      >
        <div className="mobile-offcanvas-header">
          {currentView !== "main" && (
            <button
              className="mobile-offcanvas-back-button"
              onClick={handleBackClick}
              aria-label={t("common.back", "Back")}
            >
              <BackIcon />
            </button>
          )}
          <h2 id="mobile-offcanvas-title" className="mobile-offcanvas-title">
            {currentView === "main"
              ? t("header.mobileMenuTitle", "Menu")
              : categoryViewTitle}
          </h2>
          <button
            className="mobile-offcanvas-close-button"
            onClick={onClose}
            aria-label={t("common.close", "Close")}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="mobile-offcanvas-content">
          <div
            className={`mobile-offcanvas-view mobile-offcanvas-view--main ${
              currentView === "main" ? "is-active" : ""
            }`}
          >
            <nav
              className="mobile-navigation"
              aria-label={t("header.mobileNavigation", "Mobile Navigation")}
            >
              {mobileMenu && mobileMenu.length > 0 ? (
                <ul>
                  {mobileMenu
                    .filter((item) => item.parentId === "0")
                    .sort((a, b) => a.order - b.order)
                    .flatMap((item, index) => {
                      const menuItem = (
                        <li key={item.id}>
                          <a
                            href={item.url}
                            target={item.target || "_self"}
                            rel={
                              item.target === "_blank"
                                ? "noopener noreferrer"
                                : undefined
                            }
                            onClick={onClose}
                          >
                            {item.title}
                          </a>
                        </li>
                      );

                      if (index === 0) {
                        return [
                          menuItem,
                          <li
                            key="categories-trigger"
                            className="mobile-categories-trigger-item"
                          >
                            <button
                              className="mobile-categories-trigger-button"
                              onClick={handleCategoriesClick}
                            >
                              <span>
                                {t("header.categories", "Categories")}
                              </span>
                              <ExpandIcon />
                            </button>
                          </li>,
                        ];
                      }
                      return menuItem;
                    })}
                </ul>
              ) : (
                <ul>
                  <li
                    key="categories-trigger"
                    className="mobile-categories-trigger-item"
                  >
                    <button
                      className="mobile-categories-trigger-button"
                      onClick={handleCategoriesClick}
                    >
                      <span>{t("header.categories", "Categories")}</span>
                      <ExpandIcon />
                    </button>
                  </li>
                </ul>
              )}
            </nav>

            <LanguageSwitcher className="mobile-language-switcher" />

            {children}
          </div>

          <div
            className={`mobile-offcanvas-view mobile-offcanvas-view--category ${
              currentView === "category" ? "is-active" : ""
            }`}
          >
            <nav
              className="mobile-category-navigation"
              aria-label={categoryViewTitle}
            >
              {isLoadingCategories ? (
                <Skeleton
                  count={5}
                  height={30}
                  style={{ marginBottom: "0.5rem" }}
                />
              ) : categoriesError ? (
                <p>{t("errors.loadingError", "Error loading data.")}</p>
              ) : currentCategoryList.length > 0 ||
                parentCategoryId === null ? (
                <ul>
                  {parentCategoryId === null && (
                    <li key="all-products-link">
                      <div className="mobile-category-item-content">
                        <a
                          href={shopPageUrl}
                          className="mobile-category-link"
                          onClick={onClose}
                        >
                          {t("filters.allProducts", "All Products")}
                        </a>
                      </div>
                    </li>
                  )}

                  {currentCategoryList.map((category) => {
                    const hasChildren =
                      category.children && category.children.length > 0;
                    return (
                      <li key={category.id}>
                        <div className="mobile-category-item-content">
                          <a
                            href={category.link}
                            className="mobile-category-link"
                            onClick={onClose}
                          >
                            {category.name}
                          </a>
                          {hasChildren && (
                            <button
                              className="mobile-category-expand-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExpandClick(category.id);
                              }}
                              aria-label={t(
                                "header.expandSubcategories",
                                `Expand ${category.name}`
                              )}
                            >
                              <ExpandIcon />
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                currentView === "category" && (
                  <p>
                    {t("header.noSubcategories", "No subcategories found.")}
                  </p>
                )
              )}
            </nav>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileOffcanvas;
